/* WHAT A VERDICT MEANS ON SCREEN. One place, because two screens read it.
 *
 * `subtask_verdict.decision` is written by two passes that mean different
 * things by the same word — migration 003 and `services.NEEDS_A_PERSON` say it
 * at length. The short version:
 *
 *   HUMAN_REVIEW / AUDIT / REJECT   nobody has looked yet -> a person must
 *   RECHECK (from adjudication)     LLM-k REWROTE the label -> settled, ships
 *   AUTO_ACCEPT                     the two models agreed
 *
 * THE BUG THIS FILE EXISTS TO CLOSE. The queue counted only the first group
 * (server-side, since migration 003) while the workspace drew ▲ on everything
 * that was not AUTO_ACCEPT. `Cleaning a bathroom` read "10 flagged" on its
 * queue card and "▲ 75 flagged by AI" once opened — 7.5x, and 65 of the 75 were
 * events the AI had finished with. Same shape as the 6.6x that migration 003
 * fixed on the SQL side and never followed through here.
 *
 * TWO MARKS, NOT ONE. Collapsing RECHECK into the quiet lane would have been
 * simpler, but `ingest_adjudication` chose RECHECK over AUTO_ACCEPT precisely
 * so an AI rewrite cannot reach a reviewer looking unremarkable. Both intents
 * survive if the two states are drawn differently: ▲ is the workload and
 * matches the queue's number exactly; ✎ is "the AI changed this", visible but
 * not counted as work.
 *
 * KEEP `NEEDS_A_PERSON` IN STEP WITH `app/api/services.py`. If the scoring
 * pipeline ever writes verdicts, RECHECK becomes ambiguous again and the two
 * passes must be told apart by `verification_run.verifier_version` — in the
 * table, not by widening either of these sets. */

import type { Decision, Subtask, Verdict } from "../api/types";

/** The decisions that mean a PERSON has to look. Mirrors
 *  `services.NEEDS_A_PERSON` — `test_the_flagging_set_matches_the_sql_views`
 *  guards the server's copy against the SQL views, and `verdict.test.ts`
 *  guards this one against that list. */
export const NEEDS_A_PERSON: readonly Decision[] =
  ["HUMAN_REVIEW", "AUDIT", "REJECT"] as const;

/** The AI rewrote the label. Settled — it goes to the client either way. */
export const WAS_CORRECTED: readonly Decision[] = ["RECHECK"] as const;

/** ▲ — counted on the queue card, and the number the annotator is working to. */
export const needsAPerson = (v: Verdict | null | undefined): boolean =>
  !!v && NEEDS_A_PERSON.includes(v.decision);

/** ✎ — shown, never counted as workload. */
export const wasCorrected = (v: Verdict | null | undefined): boolean =>
  !!v && WAS_CORRECTED.includes(v.decision);

/** Which of the three marks a sub-task carries, or null for no verdict at all.
 *  `null` is not "clean" — it is "nothing judged this", which is the truth for
 *  a run the adjudication pass never covered. */
export type Mark = "needs-person" | "corrected" | "confident";

export function markFor(v: Verdict | null | undefined): Mark | null {
  if (!v) return null;
  if (needsAPerson(v)) return "needs-person";
  if (wasCorrected(v)) return "corrected";
  return "confident";
}

export const MARK_GLYPH: Record<Mark, string> = {
  "needs-person": "▲",
  corrected: "✎",
  confident: "○",
};

/** The reviewer's words, not the pipeline's. */
export const MARK_LABEL: Record<Mark, string> = {
  "needs-person": "Needs a human",
  corrected: "Corrected by AI",
  confident: "AI confident",
};

export const countMarks = (subs: Subtask[]) => ({
  needsPerson: subs.filter((s) => needsAPerson(s.verdict)).length,
  corrected: subs.filter((s) => wasCorrected(s.verdict)).length,
});
